<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { DollarSign, Filter, Plus, Trash2 } from "@lucide/svelte"

  import TaskTemplateFilterDialog from "@/components/task-template-filter-dialog.svelte"
  import { Badge } from "@/components/ui/badge"
  import { Button } from "@/components/ui/button"
  import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { assignTaskTemplatePath, editTaskTemplatePath, newTaskTemplatePath, taskTemplatesPath } from "@/routes"
  import type { BreadcrumbItem, TaskTemplate, User } from "@/types"
  import { formatTaskLimit } from "@/utils"

  interface Props {
    taskTemplates: Array<TaskTemplate & { hasPendingInstance?: boolean }>
    members?: User[]
  }

  let { taskTemplates: allTaskTemplates, members = [] }: Props = $props()

  let assignTaskTemplateId = $state<number | null>(null)
  let assignDialogRef: HTMLDialogElement | null = $state(null)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Task Templates",
      href: taskTemplatesPath(),
    },
  ]

  let deleteDialogOpen = $state(false)
  let pendingDeleteTemplateId: number | null = $state(null)

  let filterDialogOpen = $state(false)
  let frequencyTypeFilters = $state(["periodic", "ad_hoc"] as Array<"periodic" | "ad_hoc">)
  let statusFilters = $state(["active"] as Array<"active" | "archived">)
  let memberFilters = $state<number[]>([])

  const filteredTemplates = $derived(
    allTaskTemplates.filter((template) => {
      if (!frequencyTypeFilters.includes(template.frequency_type)) return false
      if (!statusFilters.includes(template.status)) return false
      if (memberFilters.length > 0) {
        if (!template.available_to_id || !memberFilters.includes(template.available_to_id)) {
          return false
        }
      }
      return true
    }),
  )

  function deleteTemplate(templateId: number) {
    pendingDeleteTemplateId = templateId
    deleteDialogOpen = true
  }

  function confirmDelete() {
    if (pendingDeleteTemplateId !== null) {
      router.delete(taskTemplatesPath({ id: pendingDeleteTemplateId }))
    }
  }

  function handleAssignClick(templateId: number) {
    const template = allTaskTemplates.find((t) => t.id === templateId)
    if (!template) return

    if (template.available_to_id) {
      router.post(
        assignTaskTemplatePath(templateId),
        {},
        {
          preserveScroll: true,
        },
      )
    } else {
      assignTaskTemplateId = templateId
    }
  }

  function handleAssignSubmit(userId: number) {
    if (!assignTaskTemplateId) return

    router.post(
      "/task_instances/assign_ad_hoc",
      {
        task_template_id: assignTaskTemplateId,
        user_id: userId,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          assignTaskTemplateId = null
        },
      },
    )
  }

  function handleAssignCancel() {
    if (assignDialogRef) {
      assignDialogRef.close()
    }
    assignTaskTemplateId = null
  }

  $effect(() => {
    if (assignDialogRef && assignTaskTemplateId) {
      assignDialogRef.showModal()
      setTimeout(() => {
        const select = document.getElementById("assign_user_template") as HTMLSelectElement
        if (select) {
          select.focus()
        }
      }, 0)
    }
  })
</script>

<svelte:head>
  <title>Task Templates - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold">Task Templates</h1>
        <p class="text-muted-foreground mt-2">Manage chore templates</p>
      </div>
      <div class="flex gap-2">
        <Button variant="outline" onclick={() => (filterDialogOpen = true)}>
          <Filter class="h-4 w-4" />
          Filter
        </Button>
        <Button href={newTaskTemplatePath()}>
          <Plus class="h-4 w-4" />
          New Template
        </Button>
      </div>
    </div>

    {#if filteredTemplates.length === 0}
      <Card>
        <CardContent class="flex flex-col items-center justify-center p-12">
          <p class="text-muted-foreground mb-4">No task templates yet</p>
          <Button href={newTaskTemplatePath()}>Create your first template</Button>
        </CardContent>
      </Card>
    {:else}
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {#each filteredTemplates as template (template.id)}
          <Card>
            <CardHeader>
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <CardTitle>{template.name}</CardTitle>
                  {#if template.description}
                    <CardDescription class="mt-1">{template.description}</CardDescription>
                  {/if}
                </div>
                <Badge variant={template.status === "active" ? "default" : "outline"}>
                  {template.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent class="space-y-2">
              <div class="text-sm">
                <span class="text-muted-foreground">Type:</span> {template.frequency_type}
              </div>
              <div class="text-sm flex items-center gap-1">
                <span class="text-muted-foreground">Wage:</span>
                <div class="flex items-center gap-1">
                  <DollarSign class="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{Number.parseFloat(template.wage).toFixed(2)}</span>
                </div>
              </div>
              {#if template.available_to}
                <div class="text-sm">
                  <span class="text-muted-foreground">Available to:</span> {template.available_to.name}
                </div>
              {/if}
              {#if formatTaskLimit(template.min_instances, template.max_instances)}
                <div class="text-sm">
                  <span class="text-muted-foreground">Limit:</span> {formatTaskLimit(
                    template.min_instances,
                    template.max_instances,
                  )}
                </div>
              {/if}
              <div class="flex gap-2 pt-2">
                {#if !template.hasPendingInstance}
                  <Button
                    variant="default"
                    onclick={() => handleAssignClick(template.id)}
                    class="flex-1"
                  >
                    Assign
                  </Button>
                {/if}
                <Button variant="outline" href={editTaskTemplatePath(template.id)} class="flex-1">
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onclick={() => deleteTemplate(template.id)}
                  class="flex-1"
                >
                  <Trash2 class="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        {/each}
      </div>
    {/if}
  </div>

  <!-- Assign Dialog -->
  {#if assignTaskTemplateId}
    <dialog
      bind:this={assignDialogRef}
      onclose={() => {
        assignTaskTemplateId = null
      }}
      onkeydown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault()
          handleAssignCancel()
        }
      }}
      class="bg-background fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
    >
      <form
        method="dialog"
        onsubmit={(e) => {
          e.preventDefault()
          const select = document.getElementById("assign_user_template") as HTMLSelectElement
          const userId = Number.parseInt(select.value)
          if (userId) {
            handleAssignSubmit(userId)
          }
        }}
      >
        <h2 class="text-lg font-semibold mb-4">Assign Task</h2>
        <p class="text-sm text-muted-foreground mb-4">
          Select a member to assign this task to
        </p>
        <div class="space-y-4">
          <div>
            <label for="assign_user_template" class="block text-sm font-medium mb-2">Member</label>
            <select
              id="assign_user_template"
              name="assign_user_template"
              required
              class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Select a member</option>
              {#each members as member (member.id)}
                <option value={member.id}>{member.name}</option>
              {/each}
            </select>
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              onclick={handleAssignCancel}
              class="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Assign
            </button>
          </div>
        </div>
      </form>
    </dialog>
  {/if}

<style>
  dialog::backdrop {
    background-color: rgb(0 0 0 / 0.5);
    backdrop-filter: blur(4px);
  }
</style>

  <ConfirmationDialog
    bind:open={deleteDialogOpen}
    title="Delete Task Template"
    description="Are you sure you want to delete this task template?"
    variant="destructive"
    confirmLabel="Delete"
    onConfirm={confirmDelete}
  />

  <TaskTemplateFilterDialog
    bind:open={filterDialogOpen}
    {frequencyTypeFilters}
    {statusFilters}
    {memberFilters}
    {members}
    onFrequencyTypeChange={(filters) => {
      frequencyTypeFilters = filters
    }}
    onStatusChange={(filters) => {
      statusFilters = filters
    }}
    onMemberChange={(filters) => {
      memberFilters = filters
    }}
    onClose={() => {
      filterDialogOpen = false
    }}
  />
</AppLayout>
